"use client"

import React from 'react'
import { UIButton } from '../buttons/ui-button'
import { UITooltip } from '../overlay/ui-tooltip'
import {
  UIDropdownMenu,
  UIDropdownMenuContent,
  UIDropdownMenuItem,
  UIDropdownMenuSeparator,
  UIDropdownMenuTrigger,
} from '../forms/ui-dropdown-menu'
import { Moon, Sun, Settings, Globe } from 'lucide-react'
import { cn } from '../../lib/utils'
import { useDarkMode } from '../hooks/use-dark-mode'
import { UITypography } from '../display/ui-typography'
import { UIBox } from './ui-box'

export interface UIFloatingSettingsOption {
  value: string
  label: string
  icon?: React.ReactNode
}

export interface UIFloatingSettingsProps {
  className?: string
  showDarkModeToggle?: boolean
  showLanguageToggle?: boolean
  darkModeTooltip?: string
  languageOptions?: UIFloatingSettingsOption[]
  currentLanguage?: string
  onDarkModeToggle?: () => void
  onLanguageChange?: (language: string) => void
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left'
  customActions?: Array<{
    label: string
    icon?: React.ReactNode
    onClick: () => void
    disabled?: boolean
  }>
}

const positionClasses = {
  'top-right': 'top-4 right-4',
  'top-left': 'top-4 left-4',
  'bottom-right': 'bottom-4 right-4',
  'bottom-left': 'bottom-4 left-4'
}

/**
 * 플로팅 설정 패널
 * 다크모드, 언어 전환, 커스텀 액션을 통합한 설정 컴포넌트
 */
export const UIFloatingSettings = React.forwardRef<
  HTMLButtonElement,
  UIFloatingSettingsProps
>(({
  className,
  showDarkModeToggle = true,
  showLanguageToggle = false,
  darkModeTooltip = "다크 모드 전환",
  languageOptions = [
    { value: "ko", label: "한국어", icon: <Globe className="h-4 w-4" /> },
    { value: "en", label: "English", icon: <Globe className="h-4 w-4" /> }
  ],
  currentLanguage = "ko",
  onDarkModeToggle,
  onLanguageChange,
  position = 'top-right',
  customActions = []
}, ref) => {
  const { toggle } = useDarkMode()

  const handleDarkModeToggle = () => {
    toggle()
    onDarkModeToggle?.()
  }

  const handleLanguageChange = (language: string) => {
    onLanguageChange?.(language)
  }

  // If only dark mode is needed, show simple toggle
  if (!showLanguageToggle && customActions.length === 0) {
    return (
      <UIBox className={cn("fixed z-50", positionClasses[position], className)}>
        <UITooltip content={darkModeTooltip}>
          <UIButton
            ref={ref}
            variant="outline"
            size="icon"
            className="rounded-full w-12 h-12 shadow-lg hover:shadow-xl transition-shadow bg-background/80 border-2"
            onClick={handleDarkModeToggle}
          >
            <Sun className="h-[1.2rem] w-[1.2rem] rotate-0 scale-100 transition-transform dark:-rotate-90 dark:scale-0" />
            <Moon className="absolute h-[1.2rem] w-[1.2rem] rotate-90 scale-0 transition-transform dark:rotate-0 dark:scale-100" />
            <UITypography as="span" variant="small" className="sr-only">{darkModeTooltip}</UITypography>
          </UIButton>
        </UITooltip>
      </UIBox>
    )
  }

  // If multiple features are needed, show dropdown menu
  return (
    <UIBox className={cn("fixed z-50", positionClasses[position], className)}>
      <UIDropdownMenu>
        <UITooltip content="설정">
          <UIDropdownMenuTrigger asChild>
            <UIButton
              ref={ref}
              variant="outline"
              size="icon"
              className="rounded-full w-12 h-12 shadow-lg hover:shadow-xl transition-shadow bg-background/80 border-2"
            >
              <Settings className="h-[1.2rem] w-[1.2rem]" />
              <UITypography as="span" variant="small" className="sr-only">설정</UITypography>
            </UIButton>
          </UIDropdownMenuTrigger>
        </UITooltip>

        <UIDropdownMenuContent align="end" className="w-48">
          {showDarkModeToggle && (
            <>
              <UIDropdownMenuItem onClick={handleDarkModeToggle} className="flex items-center gap-2">
                <UIBox className="relative flex-shrink-0">
                  <Sun className="h-4 w-4 rotate-0 scale-100 transition-transform dark:-rotate-90 dark:scale-0" />
                  <Moon className="absolute inset-0 h-4 w-4 rotate-90 scale-0 transition-transform dark:rotate-0 dark:scale-100" />
                </UIBox>
                <UITypography as="span" variant="small">{darkModeTooltip}</UITypography>
              </UIDropdownMenuItem>
              {(showLanguageToggle || customActions.length > 0) && <UIDropdownMenuSeparator />}
            </>
          )}

          {showLanguageToggle && languageOptions.map((option) => (
            <UIDropdownMenuItem
              key={option.value}
              onClick={() => handleLanguageChange(option.value)}
              className={cn(
                "flex items-center gap-2",
                currentLanguage === option.value ? "bg-accent" : ""
              )}
            >
              <span className="flex-shrink-0">
                {option.icon || <Globe className="h-4 w-4" />}
              </span>
              <UITypography as="span" variant="small" className="flex-1">{option.label}</UITypography>
              {currentLanguage === option.value && (
                <UITypography as="span" variant="caption" size="xs" color="muted">✓</UITypography>
              )}
            </UIDropdownMenuItem>
          ))}

          {customActions.length > 0 && showLanguageToggle && <UIDropdownMenuSeparator />}

          {customActions.map((action, index) => (
            <UIDropdownMenuItem
              key={index}
              onClick={action.onClick}
              disabled={action.disabled}
              className="flex items-center gap-2"
            >
              {action.icon && <UITypography as="span" variant="small" className="flex-shrink-0">{action.icon}</UITypography>}
              <UITypography as="span" variant="small">{action.label}</UITypography>
            </UIDropdownMenuItem>
          ))}
        </UIDropdownMenuContent>
      </UIDropdownMenu>
    </UIBox>
  )
})

UIFloatingSettings.displayName = "UIFloatingSettings"